import {
  Smartphone, ScanLine, Hand, Layers, ShieldCheck, Zap, Clock, MapPin, Star,
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServicePageLayout from '../../components/services/ServicePageLayout';
import { ServiceSection, ServiceFAQ, RelatedServices } from '../../components/services/ServiceUI';
import {
  ServiceHeroPro, ProofStrip, DiagnosisList, PriceCardPro, AvisStrip, LocalCTA,
} from '../../components/services/elevated';
import { REVIEWS, RATING, REVIEW_COUNT } from '../../data/reviews';

const SLUG = '/reparation-ecran-samsung-montpellier';

const faq = [
  { q: "Pourquoi l'écran de mon Galaxy S est-il plus cher qu'un écran d'iPhone ?", a: "Parce que la dalle AMOLED Samsung intègre la vitre et l'affichage en un seul bloc, plus coûteux à remplacer. Le devis est gratuit et précis selon votre modèle." },
  { q: "Remplacez-vous seulement la vitre ?", a: "Sur dalle AMOLED, la vitre seule est rarement remplaçable de façon fiable ; nous changeons le bloc complet pour garantir le résultat et le tactile." },
  { q: "Combien de temps faut-il ?", a: "En général 30 à 45 minutes selon le modèle, sans rendez-vous." },
  { q: "L'écran est-il garanti ?", a: "Oui, 6 mois." },
];

const cas = [
  { icon: Smartphone, title: "Vitre fissurée", desc: "Même si l'affichage fonctionne encore." },
  { icon: ScanLine, title: "Dalle AMOLED noire", desc: "Taches d'encre, lignes verticales." },
  { icon: Hand, title: "Tactile mort", desc: "Ne répond plus ou réagit seul." },
  { icon: Layers, title: "Bordures décollées", desc: "Après un choc ou une chute." },
];

const proofItems = [
  { icon: Zap, label: 'Réparé en 30–45 min' },
  { icon: ShieldCheck, label: 'Garantie 6 mois' },
  { icon: Clock, label: 'Sans rendez-vous' },
  { icon: Star, label: '4,7/5 · 579 avis' },
  { icon: MapPin, label: 'Centre-ville Montpellier' },
];

const ReparationEcranSamsung = () => (
  <ServicePageLayout trail={[{ name: 'Accueil', to: '/' }, { name: 'Réparation Samsung Galaxy', to: '/reparation-samsung-montpellier' }, { name: "Réparation d'écran" }]}>
    <Seo
      title="Réparation Écran Samsung Galaxy Montpellier | AlloRép."
      description="Réparation écran Samsung Galaxy à Montpellier : dalle AMOLED, vitre, tactile. Séries S et A. Devis gratuit, garantie 6 mois."
      canonicalPath={SLUG}
      service={{
        name: "Réparation d'écran Samsung Galaxy à Montpellier",
        serviceType: "Remplacement d'écran Samsung Galaxy",
        description: "Remplacement d'écran (dalle AMOLED, vitre, tactile) Samsung Galaxy à Montpellier, séries S, A, Note et Z.",
        offers: { '@type': 'Offer', priceCurrency: 'EUR', price: '120', description: 'À partir de 120 € selon la gamme Galaxy' },
      }}
      faq={faq}
      breadcrumb={[
        { name: 'Accueil', path: '/' },
        { name: 'Réparation Samsung Galaxy', path: '/reparation-samsung-montpellier' },
        { name: "Réparation d'écran Samsung Galaxy à Montpellier", path: SLUG },
      ]}
    />

    <ServiceHeroPro
      eyebrow="Écran Samsung — Montpellier"
      h1Before="Réparation d'"
      h1Highlight="écran Samsung Galaxy"
      h1After=" à Montpellier"
      intro="Écran Galaxy fissuré, dalle AMOLED noire ou tactile qui ne répond plus ? AlloRéparations remplace l'écran de votre Samsung Galaxy à Montpellier, sans rendez-vous, avec devis gratuit et garantie 6 mois. Nous traitons aussi bien les séries A que les Galaxy S et Note."
      image={{ src: '/iphones/samsung-galaxy.png', alt: "Réparation d'écran Samsung Galaxy à Montpellier — dalle AMOLED" }}
      rating={{ value: RATING, count: REVIEW_COUNT }}
    />

    <ProofStrip items={proofItems} />

    <ServiceSection title="Pourquoi un écran Samsung coûte-t-il plus cher ?">
      <p>
        La plupart des Galaxy utilisent une dalle AMOLED dans laquelle la vitre et l'écran ne font qu'un bloc.
        Quand la vitre casse, c'est souvent toute la dalle qu'il faut remplacer — d'où un tarif supérieur à celui
        d'un écran LCD classique. Nous vous indiquons d'emblée le coût exact pour votre modèle, sans surprise.
      </p>
    </ServiceSection>

    <DiagnosisList title="Les pannes d'écran que nous réparons" items={cas} />

    <PriceCardPro
      eyebrow="Tarifs par gamme"
      title="Prix par gamme"
      columns={['Gamme Galaxy', 'Délai', 'Prix TTC']}
      rows={[
        ['Écran Galaxy A (A13, A53…)', '30–45 min', 'dès 120 €'],
        ['Écran Galaxy S (S21, S22, S23…)', '45 min', 'dès 180 €'],
        ['Écran Galaxy Note', '45 min', 'sur devis'],
        ['Écran Galaxy Z (Fold / Flip)', 'sur devis', 'sur devis'],
      ]}
    />

    <AvisStrip rating={RATING} count={REVIEW_COUNT} reviews={REVIEWS.slice(3, 6)} />

    <ServiceFAQ faq={faq} />

    <LocalCTA heading="Écran Galaxy cassé ? Devis exact, sans surprise." sub="Dalle AMOLED remplacée en 30 à 45 minutes, garantie 6 mois." />

    <RelatedServices
      links={[
        { label: 'Réparation Samsung Galaxy (toutes pannes)', to: '/reparation-samsung-montpellier', desc: 'La page pilier Samsung.' },
        { label: 'Micro-soudure & carte mère', to: '/micro-soudure-montpellier', desc: "Ne s'allume plus, charge HS." },
      ]}
    />
  </ServicePageLayout>
);

export default ReparationEcranSamsung;
